<template>
  <div>
    <!-- Filters -->
    <b-card>
      <filters
        :customFiltersShow="true"
        :originImprovementFilterShow="true"
        :locationFilterShow="true"
        :dateRangeFilterShow="true"
        :resetDateButtonShow="true"
        :locationDefaultValue="true"
        :supervisorFilterShow="true"
        :origin="'commitment'"
        supervisorFilterPlaceholder="creator_filter"
        :physicalLocationFilterShow="true"
        :areaFilterShow="true"
        @changeFilter="updateFilter"
      />
    </b-card>

    <!-- Statistic Cards -->
    <b-row>
      <b-col sm="3">
        <statistic-card-vertical-total
          :statistic="statisticCardsData.total === undefined ? '-' : statisticCardsData.total"
          :isButton="true"
          :isSelected="activeState === 'total'"
          @filter="filterByState('total')"
        >
        </statistic-card-vertical-total>
      </b-col>

      <b-col sm="3">
        <statistic-card-vertical-completed
          :statistic="statisticCardsData.completed === undefined ? '-' : statisticCardsData.completed"
          :isButton="true"
          :isSelected="activeState === 'completed'"
          @filter="filterByState('completed')"
          >
        </statistic-card-vertical-completed>
      </b-col>

      <b-col sm="3">
        <statistic-card-vertical-pending
          :statistic="statisticCardsData.pending === undefined ? '-' : statisticCardsData.pending"
          :isButton="true"
          :isSelected="activeState === 'pending'"
          @filter="filterByState('pending')"
        >
        </statistic-card-vertical-pending>
      </b-col>

      <b-col sm="3">
        <statistic-card-vertical-overdue
          :statistic="statisticCardsData.overdue === undefined ? '-' : statisticCardsData.overdue"
          :isButton="true"
          :isSelected="activeState === 'overdue'"
          @filter="filterByState('overdue')"
        >
        </statistic-card-vertical-overdue>
      </b-col>
 
    </b-row>

    <!-- Commitments List -->
    <b-card class="pb-0 mb-1">
      <b-row class="pl-1">

        <!-- Search -->
        <b-col md="3">
          <b-form-group>
            <b-form-input
              v-model="searchQuery"
              type="search"
              class="d-inline-block"
              :placeholder="$t('message.search')"
              @input="searchTask"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Table -->
      <b-col cols="12">
        <b-table
          ref="refCommitmentListTable"
          :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
          show-empty=""
          :items="paginatedCommitments"
          hover
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
          @row-clicked="handleCommitmentClick"
        >

          <!-- Creation date -->
          <template #cell(creationDate)="data">
            {{ data.item.creationDateString }}
          </template>

          <!-- Title -->
          <template #cell(formatTitle)="data">
            <span v-b-tooltip.hover.v-primary.top="data.item.title">
              {{ data.item.formatTitle }}
            </span>
          </template>

          <!-- State -->
          <template #cell(state)="data">
            <h6 :class="`text-nowrap mr-1 ${setDateColor(data.item.dueDate, data.item.completed)}`">{{ $t(`${data.item.state}`)  }}</h6>
          </template>

          <!-- Action buttons -->
          <template #cell(actions)="data">
            <div class="d-flex align-items-center justify-content-start">
              <b-form-checkbox
                :checked="data.item.completed"
                @click.native.stop
                v-b-tooltip.hover.v-primary="!data.item.completed ? $t('mark_complete') : null"
                @change="updateCommitmentCompleted(data.item)"
                style="transform: scale(0.9); "
              />
              <ViewButton @clicked="handleCommitmentClick(data.item)"/>
              <DeleteButton @clicked="commitmentToDelete = data.item" v-b-modal="modalIdToDeletd"/>
            </div>
          </template>
        </b-table>
      </b-col>

      <!-- Modal to confirm delete action -->
      <b-modal
        :id="modalIdToDeletd"
        centered
        no-close-on-backdrop
        modal-class="modal-danger"
        ok-variant="danger"
        cancel-variant="outline-secondary"
        :title="$t('message.confirm_action')"
        :ok-title="$t('message.delete')"
        :cancel-title="$t('message.cancel')"
        @ok="removeCommitment(commitmentToDelete)"
      > {{ $t('message.confirm_delete_commitment') }}
      </b-modal>
      
      <!-- Commitment Sidebar -->
      <commitment-handler-sidebar
        v-model="isCommitmentHandlerSidebarActive"
        :commitment="commitment"
        :commitmentIndex="0"
        :clear-commitment-data="clearCommitmentData"
        handlerId="commitment-list-modal"
        origin="improvement"
        @remove-commitment="removeCommitment(commitment)"
        @update-commitment="updateCommitment"
      />

      <!-- Pagination -->
      <b-col
        cols="12"
        class="d-flex justify-content-between flex-wrap"
      >
        <div class="mb-1">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="[5,10,15,20,25,30,40,50,100]"
              class="w-50"
            />
          </b-form-group>
        </div>
        <div class="mb-1">
          <span class="text-muted">{{ $tc('message.paginationText', 0, { from: `${((currentPage * perPage) - perPage) + (commitments.length ? 1 : 0)}`, to: `${commitments.length + ((currentPage * perPage) - perPage)}`, total: `${totalCommitments}` }) }}</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCommitments"
          :per-page="perPage"
          first-number=""
          last-number=""
          prev-class="prev-item"
          next-class="next-item"
        />
      </b-col>
    </b-card>

    <!-- Excel -->
    <b-col md="2" class="pb-1 pl-0">
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="secondary"
        block    
      >
        <json-excel :data="dataExcel" :fields="excelFields">
          {{ $t('download_xlsx') }}
        </json-excel>
      </b-button>
    </b-col>
  </div>
</template>

<script>
import store from '@/store'
import { ref, watch, computed, onMounted } from '@vue/composition-api'
import { BFormInput, BBadge, BTable, BPagination, VBModal, BFormCheckbox, VBTooltip } from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { useRouter } from '@core/utils/utils'
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import useCommon from "@/views/organization/useCommon"
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import Filters from "@/views/organization/Filters.vue"
import StatisticCardVerticalTotal from '@/views/habit/improvement/charts/StatisticCardVerticalTotal.vue'
import StatisticCardVerticalCompleted from '@/views/habit/improvement/charts/StatisticCardVerticalCompleted.vue'
import StatisticCardVerticalPending from '@/views/habit/improvement/charts/StatisticCardVerticalPending.vue'
import StatisticCardVerticalOverdue from '@/views/habit/improvement/charts/StatisticCardVerticalOverdue.vue'
import Ripple from 'vue-ripple-directive'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'
import ViewButton from "@/views/components/Shared/Buttons/ViewButton.vue"
import JsonExcel from "vue-json-excel"
import CommitmentHandlerSidebar from '@/views/habit/confirmation/shared/sidebars/CommitmentHandlerSidebar.vue'

export default {
  components: {
    BFormInput,
    BBadge,
    BTable,
    BPagination,
    BFormCheckbox,
    VuePerfectScrollbar,
    Loading,
    StatisticCardVerticalCompleted,
    StatisticCardVerticalPending,
    StatisticCardVerticalTotal,
    StatisticCardVerticalOverdue,
    Filters,
    DeleteButton,
    ViewButton,
    JsonExcel,
    CommitmentHandlerSidebar
  },
  directives: {
    Ripple,
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
  },
  setup() {
    const { resolveTagVariant, sendEmailImprovementsOrCommitments } = useCommonTodo()
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { getItemsAggregate, updateItem } = useLambda()
    const { getDatesFromRange } = useCommonDashboards()
    const { handleError, formatIsoDate } = useCommon()
    const { route } = useRouter()

    const TODO_APP_STORE_MODULE_NAME = 'app-todo'
    const commitmentToDelete = ref(null)
    const modalIdToDeletd = 'delete-commitment-modal'
    const userData = store.state?.userStore?.userData
    const clientId = userData.role !== "admin" ? userData.client.$oid : null
    const userRole = userData.role
    const isLoading = ref(true)
    const now = new Date()
    const nowPlusOneWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7)
    const dateRangeFilter = ref('')
    const collection = 'commitment'
    const perPage = ref(20)
    const currentPage = ref(1)
    const sortBy = ref('')
    const isSortDirDesc = ref(false)
    const commitments = ref([])
    const totalCommitments = ref(0)
    const statisticCardsData = ref({})
    const supervisorFilter = ref([])
    const userLocations = userData.locations?.length ? userData.locations : null
    const locationFilter = ref(userLocations ? userLocations.map(e => e.value) : [])
    const physicalLocationFilter = ref([])
    const commitmentId = route.value.query.originId
    const originFilter = ref([])
    const roleFilter = ref([])
    const dataExcel = ref([])
    const optionsMetadata = computed(() => store.state[TODO_APP_STORE_MODULE_NAME].optionsMetadata)
    const isCommitmentHandlerSidebarActive = ref(false)
    const searchQuery = ref("")
    const perfectScrollbarSettings = { maxScrollbarLength: 150, }
    const activeState = ref('total')
    const originOptions = computed(() => [
      { title: `${i18n.t('improvement')} – ${i18n.t('message.direct')}`, value: 'direct' },
      { title: `${i18n.t('improvement')} – ${i18n.t('message.daily_dialogue')}`, value: 'daily_dialogue' }
      // { title: i18n.t('message.AI assistance'), value: 'AI assistance' },
      // { title: i18n.t('message.confirmation'), value: 'confirmation' },
    ])
    let blankTaskCommitment = {
      client_id: clientId,
      title: '',
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      description: '',
      assignee: null,
      completed: false,
      deleted: false,
    }
    const commitment = ref(JSON.parse(JSON.stringify(blankTaskCommitment)))

    const clearCommitmentData = () => {
      commitment.value = JSON.parse(JSON.stringify(blankTaskCommitment))
    }

    const fetchCommitments = async (searchValue = '') => {
      isLoading.value = true
      try {
        const initialQuery = {
          client_id: oid(clientId),
          deleted: { $ne: true },
        }
        // Due date
        const dueDates = getDatesFromRange(dateRangeFilter.value)
        if (dueDates[0]) {
          initialQuery.dueDate = { $gte: date(dueDates[0]), $lt: date(dueDates[1]) }
        }
        // Created by
        if (supervisorFilter.value.length > 0) {
          initialQuery.created_by = { $in: supervisorFilter.value.map(e => oid(e)) }
        }

        // Origin by 'behaviour'
        if (originFilter.value && originFilter.value?.length && originFilter.value === 'confirmation') {
          initialQuery.behaviour = { $exists: true }
        }
        
        const finalQuery = {}
        
        // Location
        if (locationFilter.value.length) {
          finalQuery['assignee.locations._id'] = { $in: locationFilter.value }
        }

        if (physicalLocationFilter.value.length) {
          finalQuery['assignee.physical_locations._id'] = { $in: physicalLocationFilter.value }
        }

        // Origin
        if (originFilter.value && originFilter.value?.length && originFilter.value !== 'confirmation' ) {
          finalQuery['improvement.origin'] = originFilter.value
        }
  
        const pipeline = [
          { $match: initialQuery },
          {
            $lookup: {
              from: 'worker',
              localField: 'assignee',
              foreignField: '_id',
              pipeline: [
                { $project: { name: 1, email: 1, locations: 1, physical_locations: 1, } },
                {
                  $lookup: {
                    from: 'location',
                    localField: 'locations',
                    foreignField: '_id',
                    pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
                    as: 'locations',
                  },
                },
                {
                  $lookup: {
                    from: 'physical_location',
                    localField: 'physical_locations',
                    foreignField: '_id',
                    pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }],
                    as: 'physical_locations'
                  }
                },
                { $addFields: { _id: { $toString: "$_id" } } }
              ],
              as: 'assignee'
            }
          },
          {
            $lookup: {
              from: 'improvement',
              localField: 'improvement',
              foreignField: '_id',
              pipeline: [
                { $project: { origin: 1 } },
              ],
              as: 'improvement'
            }
          },
          { 
            $unwind: { 
              path: "$improvement", 
              preserveNullAndEmptyArrays: true 
            } 
          },
          {
            $lookup: {
              from: 'worker',
              localField: 'created_by',
              foreignField: '_id',
              pipeline: [
                { $project: { name: 1, email: 1 } },
                { $addFields: { _id: { $toString: "$_id" } } }
              ],
              as: 'created_by'
            }
          },
          { $match: finalQuery },
          { $addFields: { _id: { $toString: "$_id" }, assignee: { $arrayElemAt: ["$assignee", 0] } } },
          { $sort: { dueDate: 1 } }
        ]
  
        const items = await getItemsAggregate({ collection, pipeline })
        if (!items) return
  
        // Search
        if (searchValue && searchValue !== searchQuery.value) return
        commitments.value  = searchQuery.value ? items.filter(e => e.title?.toLowerCase().includes(searchQuery.value.toLowerCase())) : items

        // Calculate statistic card values
        let completedTasks = 0
        let pendingTasks = 0
        let overdueTasks = 0
        let dateNow = new Date()

        commitments.value.forEach(c => {
          c.creationDateString = c.creationDate ? formatIsoDate(c.creationDate) : '-',
          c.created_by = c.created_by[0] ? c.created_by[0].name : '-',
          c.assigneeName = c.assignee?.name
          c.assigneeLocations = c.assignee?.locations?.map(e => e.location).join(', ')
          c.assigneePhysicalLocations = c.assignee?.physical_locations ? c.assignee.physical_locations.map(e => e.name).join(', ') : '-'
          c.assignee = c.assignee ? c.assignee._id : null
          c.formatDueDate = c.dueDate ? formatIsoDate(c.dueDate) : ''
          c.formatTitle = c.title && c.title?.length > 40 ? `${c.title.slice(0, 40)}...` : c.title || ''
          c.originName = c.improvement?.origin
          
          if(c.improvement?.origin === 'daily_dialogue' ||  c.improvement?.origin === 'direct') {
            const originTranslated = `${i18n.t('improvement')} – ${i18n.t(`message.${c.improvement.origin}`)}`
            c.originName = originTranslated.charAt(0).toUpperCase() + originTranslated.slice(1)
          }
          // Origin: "behaviour" can be added if needed
          else if(c.confirmation) {
            const originTranslated = i18n.t('Confirmation')
            c.originName = originTranslated.charAt(0).toUpperCase() + originTranslated.slice(1)
          }

          if (c.completed) {
            completedTasks++
            c.state = 'completed'
          }
          else if (c.dueDate && (c.dueDate < dateNow)) {
            overdueTasks++
            c.state = 'overdue'
          } else {
            pendingTasks++
            c.state = 'pending'
          }
        })

        // Sort by state (first overdue, then pending, then completed)
        commitments.value.sort((a, b) => {
          if (a.state === 'overdue' && b.state !== 'overdue') return -1
          if (a.state === 'pending' && b.state === 'completed') return -1
          if (a.state === 'completed' && b.state !== 'completed') return 1
          return 0
        })

        dataExcel.value = commitments.value
        totalCommitments.value = completedTasks + pendingTasks + overdueTasks
        if(activeState.value !== 'total') {
          commitments.value = commitments.value?.filter(commitment => commitment.state === activeState.value)
        }
        statisticCardsData.value = {
          total: totalCommitments.value,
          completed: completedTasks,
          pending: pendingTasks,
          overdue: overdueTasks
        }
        store.commit('app-todo/SET_STATISTIC_CARDS_DATA', statisticCardsData) 
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_improvement_list') })
      } finally {
        if (!searchValue || searchValue === searchQuery.value) isLoading.value = false    // Don't remove the loader if the user is still typing a search
      }
    }

    const filterByState = (state) => {
      activeState.value = state
      fetchCommitments()
    }

    const openSidebarIfCommitmentIdExists = async () => {
      if(!commitmentId) return
      try {
        const query = { _id: oid(commitmentId) }

        const pipeline = [
        { $match: query },
        {
          $addFields: {
            _id: { $toString: "$_id" },
            origin_id: { $toString: "$origin_id" },
            assistance: { $toString: "$assistance" },
            assignee: { $toString: "$assignee" },
            subscribers: {
              $map: {
                input: "$subscribers",
                as: "id",
                in: { $toString: "$$id" }
              }
            }
          }
        }
      ]
      
        const items = await getItemsAggregate({ collection, pipeline })

        commitment.value = items[0]
        isCommitmentHandlerSidebarActive.value = true
      } catch (error) {
        console.log(error)
      }
    }

    const updateCommitmentCompleted = commitmentData => {
      commitmentData.completed = !commitmentData.completed
      updateCommitment(commitmentData, "update")
    }

    const removeCommitment = (commitmentData) => {
      if (!commitmentData.deleted) {
        commitmentData.deleted = true
        updateCommitment(commitmentData, "delete")
      }
    }

const updateCommitment = async (commitmentData, action) => {
      try {
        const query = { _id: oid(commitmentData._id) }
        const setPayload = {
          title: commitmentData.title || '',
          assignee: oid(commitmentData.assignee),
          completed: commitmentData.completed || false,
          deleted: commitmentData.deleted || false,
        }
        const unsetPayload = {}

        let dueDateFormatted
        if (commitmentData.dueDate) {
          dueDateFormatted = commitmentData.dueDate instanceof Date ? commitmentData.dueDate : new Date(`${commitmentData.dueDate.slice(0, 10)} 12:00:00`)
          setPayload.dueDate = date(dueDateFormatted)
        } else {
          unsetPayload.dueDate = ''
        }

        const realmAction = { $set: setPayload, $unset: unsetPayload }
        await updateItem({ collection, query, action: realmAction })

        // Remove the reference in the improvement collection
        if (action === 'delete' && commitmentData.improvement?._id) {
          const impQuery = { _id: commitmentData.improvement._id }
          const impAction = {
            $pull: {
              commitments: oid(commitmentData._id)
            }
          }
          await updateItem({ collection: 'improvement', query: impQuery, action: impAction })
        }

        // Updates event associated with the commitment
        const eventQuery = { commitment: oid(commitmentData._id) }
        let eventAction = {}
        if (action === 'delete') {
          eventAction = { $set: { deleted: true } }
        } else {
          const dateNow = new Date()
          const state = commitmentData.completed
            ? 'Realizadas'
            : (commitmentData.dueDate && new Date(commitmentData.dueDate) < dateNow)
              ? 'Vencidas'
              : 'Pendientes'
          const endDate = dueDateFormatted ? new Date(dueDateFormatted) : ''
          if (endDate) endDate.setHours(13, 0, 0, 0)
          eventAction = {
            $set: {
              attendee: oid(commitmentData.assignee),
              title: commitmentData.title || '',
              start: setPayload.dueDate || '',
              end: date(endDate),
              extendedProps: { calendar: state },
            }
          }
        }
        await updateItem({ collection: 'event', query: eventQuery, action: eventAction })

        // Show success message
        if (action === "delete") showSuccessMessage(i18n.t('message.commitment_deleted'))
        else showSuccessMessage(i18n.t('message.commitment_updated'))

        // Refetch commitments to get updated list
        fetchCommitments()

        // Send email to assignee with the details of the commitment updated
        sendEmailImprovementsOrCommitments([commitmentData], action)
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.commitment_update_error'))
      }
    }

    const searchTask = searchValue => {
      isLoading.value = true
      fetchCommitments(searchValue)
    }

    const resetSearch = () => {
      searchQuery.value = ''
      searchTask()
    }

    const handleCommitmentClick = commitmentData => {
      commitment.value = commitmentData
      isCommitmentHandlerSidebarActive.value = true
    }

    const setDateColor = (taskDate, completed) => {
      if (completed) return "text-success"
      const date = new Date(taskDate)
      if (date < now) return "text-danger"
      // if (date > nowPlusOneWeek) return "text-muted"
      return "text-warning"
    }

    const paginatedCommitments = computed(() => {
      const start = (currentPage.value - 1) * perPage.value
      const end = start + perPage.value
      return commitments.value.slice(start, end)
    })

    const tableColumns = computed(() => {
      return [
        { key: 'creationDate', label: i18n.t('message.tableHeader.creationDate') },
        { key: 'formatDueDate', label: i18n.t('message.tableHeader.dueDate') },
        { key: 'created_by', label: i18n.t('message.tableHeader.created_by') },
        { key: 'assigneeName', label: i18n.t('message.tableHeader.assignee') },
        { key: 'assigneePhysicalLocations', label: i18n.t('message.tableHeader.assigneePhysicalLocations') },
        { key: 'assigneeLocations', label: i18n.t('message.tableHeader.assigneeLocations') },
        { key: 'formatTitle', label: i18n.t('message.tableHeader.Name') },
        { key: 'state', label: i18n.t('message.tableHeader.state') },
        { key: 'originName', label: i18n.t('message.origin') },
        { key: 'actions', label: i18n.t('message.tableHeader.actions') },
      ]
    })

    const excelFields = {
        [i18n.t('message.tableHeader.creationDate')]: "creationDateString",
        [i18n.t('message.tableHeader.created_by')]: "created_by",
        [i18n.t('message.tableHeader.assignee')]: "assigneeName",
        [i18n.t('message.tableHeader.assigneeLocations')]: "assigneeLocations",
        [i18n.t('message.tableHeader.assigneePhysicalLocations')]: "assigneePhysicalLocations",
        [i18n.t('message.tableHeader.Name')]: "title",
        [i18n.t('message.tableHeader.state')]: "state",
        [i18n.t('message.origin')]: "originName",
        [i18n.t('message.tableHeader.dueDate')]: "formatDueDate",
    }

    const updateFilter = (data) => {
      locationFilter.value = data.locationFilter
      physicalLocationFilter.value = data.physicalLocationFilter
      originFilter.value = data.originFilter
      roleFilter.value = data.roleFilter
      dateRangeFilter.value = data.dateRangeFilter
      supervisorFilter.value = data.supervisorFilter
      if(!data.locationFilter?.length && !data.originFilter?.length && !data.roleFilter?.length && !data.dateRangeFilter?.length && !data.supervisorFilter?.length) {
        activeState.value = 'total'
      } 
      fetchCommitments()
    }


    watch([locationFilter, physicalLocationFilter, dateRangeFilter, searchQuery, sortBy, isSortDirDesc], () => {
      isLoading.value = true
      fetchCommitments()
    })

    onMounted(async () => {
      fetchCommitments()
      openSidebarIfCommitmentIdExists()
    })

    return {
      commitment,
      commitments,
      totalCommitments,
      removeCommitment,
      updateCommitment,
      clearCommitmentData,
      searchQuery,
      fetchCommitments,
      perfectScrollbarSettings,
      searchTask,
      resetSearch,
      isLoading,
      // domains,
      setDateColor,
      optionsMetadata,
      resolveTagVariant,
      isCommitmentHandlerSidebarActive,
      handleCommitmentClick,
      dataExcel,
      excelFields,
      tableColumns,
      updateFilter,
      perPage,
      currentPage,
      sortBy,
      isSortDirDesc,
      paginatedCommitments,
      userRole,
      statisticCardsData,
      commitmentToDelete,
      modalIdToDeletd,
      updateCommitmentCompleted,
      filterByState,
      activeState,
      originOptions
    }
  },
}
</script>

<style lang="scss" scoped>
.draggable-task-handle {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  visibility: hidden;
  cursor: move;

  .todo-task-list .todo-item:hover & {
    visibility: visible;
  }
}
</style>

<style lang="scss">
@import "~@core/scss/base/pages/app-todo.scss";
</style>

<style lang="scss" scoped>
.chatbot{
  width: 2.7rem;
  margin-right: 0.5rem;
  height: auto;
  padding-left: 0.5rem;
  opacity: 0.5;
}
</style>
